import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import Navigation from "../components/Navigation";
import { ReadAloudButton } from "../components/ReadAloudButton";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sparkles,
  Sun,
  Moon,
  Star,
  Wind,
  Brain,
  Heart,
  Calendar,
  Play,
  Pause,
  RefreshCw,
  ChevronRight,
  Zap,
  Eye,
  Flame,
  Droplets,
  Mountain,
  Feather,
  Wallet,
  DollarSign,
  PiggyBank,
  TrendingUp,
  Target,
  Loader2,
  CheckCircle,
  Clock,
  Dumbbell,
  Apple,
  Activity,
} from "lucide-react";
import { toast } from "sonner";

const ZODIAC_ICONS = {
  aries: Flame, taurus: Mountain, gemini: Wind, cancer: Moon,
  leo: Sun, virgo: Feather, libra: Star, scorpio: Droplets,
  sagittarius: Zap, capricorn: Mountain, aquarius: Wind, pisces: Droplets,
};

const ZODIAC_SIGNS = [
  "aries", "taurus", "gemini", "cancer", "leo", "virgo",
  "libra", "scorpio", "sagittarius", "capricorn", "aquarius", "pisces"
];

export default function Wellness() {
  const [affirmation, setAffirmation] = useState("");
  const [personalizedAffirmation, setPersonalizedAffirmation] = useState(null);
  const [selectedSign, setSelectedSign] = useState("aries");
  const [horoscope, setHoroscope] = useState(null);
  const [breathingExercises, setBreathingExercises] = useState([]);
  const [consciousnessPractices, setConsciousnessPractices] = useState([]);
  const [hypnosisGuides, setHypnosisGuides] = useState([]);
  const [itinerary, setItinerary] = useState(null);
  const [wellnessStats, setWellnessStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBreathingExercise, setActiveBreathingExercise] = useState(null);
  const [selectedPractice, setSelectedPractice] = useState(null);
  
  // Financial Advisor State
  const [financialInfo, setFinancialInfo] = useState({
    monthly_income: "",
    monthly_expenses: "",
    debt_amount: "",
    savings_goal: "",
    current_situation: "",
  });
  const [financialAdvice, setFinancialAdvice] = useState(null);
  const [loadingFinancial, setLoadingFinancial] = useState(false);
  
  // Spiritual Routine State
  const [spiritualRoutine, setSpiritualRoutine] = useState(null);
  const [loadingSpiritual, setLoadingSpiritual] = useState(false);
  
  // Fitness State
  const [fitnessProfile, setFitnessProfile] = useState({
    fitness_level: "beginner",
    fitness_goals: [],
    current_weight: "",
    target_weight: "",
    workout_frequency: "3x/week",
    preferred_workouts: [],
    dietary_restrictions: [],
  });
  const [fitnessOptions, setFitnessOptions] = useState({});
  const [workoutPlan, setWorkoutPlan] = useState(null);
  const [mealPlan, setMealPlan] = useState(null);
  const [loadingWorkout, setLoadingWorkout] = useState(false);
  const [loadingMeal, setLoadingMeal] = useState(false);
  const [savingFitness, setSavingFitness] = useState(false);

  useEffect(() => {
    fetchWellnessData();
  }, []);

  const fetchWellnessData = async () => {
    try {
      const [affirmRes, breathingRes, consciousnessRes, hypnosisRes, statsRes] = await Promise.all([
        axios.get(`${API}/wellness/affirmation`),
        axios.get(`${API}/wellness/breathing-exercises`),
        axios.get(`${API}/wellness/consciousness`),
        axios.get(`${API}/wellness/hypnosis`),
        axios.get(`${API}/wellness/stats`).catch(() => ({ data: null })),
      ]);
      
      setAffirmation(affirmRes.data?.affirmation || "");
      setBreathingExercises(breathingRes.data?.exercises || []);
      setConsciousnessPractices(consciousnessRes.data?.practices || []);
      setHypnosisGuides(hypnosisRes.data?.guides || []);
      if (statsRes.data) setWellnessStats(statsRes.data);
    } catch (error) {
      console.error("Wellness data fetch error:", error);
      toast.error("Failed to load wellness data");
      // Set fallback empty arrays to prevent map errors
      setBreathingExercises([]);
      setConsciousnessPractices([]);
      setHypnosisGuides([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHoroscope = async (sign) => {
    try {
      const response = await axios.get(`${API}/wellness/horoscope/${sign}`);
      setHoroscope(response.data);
    } catch (error) {
      toast.error("Failed to load horoscope");
    }
  };

  const fetchPersonalizedAffirmation = async () => {
    try {
      const response = await axios.post(`${API}/wellness/affirmation/personalized`);
      setPersonalizedAffirmation(response.data);
      toast.success("Personalized affirmation generated!");
    } catch (error) {
      toast.error("Failed to generate affirmation");
    }
  };

  const fetchItinerary = async () => {
    try {
      const response = await axios.post(`${API}/wellness/goal-itinerary`);
      setItinerary(response.data);
    } catch (error) {
      toast.error("Failed to generate itinerary");
    }
  };

  const fetchFinancialAdvice = async () => {
    setLoadingFinancial(true);
    try {
      const response = await axios.post(`${API}/wellness/financial-advisor`, {
        monthly_income: financialInfo.monthly_income ? parseFloat(financialInfo.monthly_income) : null,
        monthly_expenses: financialInfo.monthly_expenses ? parseFloat(financialInfo.monthly_expenses) : null,
        debt_amount: financialInfo.debt_amount ? parseFloat(financialInfo.debt_amount) : null,
        savings_goal: financialInfo.savings_goal ? parseFloat(financialInfo.savings_goal) : null,
        current_situation: financialInfo.current_situation || null,
      });
      setFinancialAdvice(response.data);
      toast.success("Financial advice generated!");
    } catch (error) {
      toast.error("Failed to generate financial advice");
    } finally {
      setLoadingFinancial(false);
    }
  };

  const fetchSpiritualRoutine = async () => {
    setLoadingSpiritual(true);
    try {
      const response = await axios.get(`${API}/wellness/spiritual-routine`);
      setSpiritualRoutine(response.data);
    } catch (error) {
      toast.error("Failed to load spiritual routine");
    } finally {
      setLoadingSpiritual(false);
    }
  };

  const fetchPersonalizedSpiritualRoutine = async () => {
    setLoadingSpiritual(true);
    try {
      const response = await axios.post(`${API}/wellness/spiritual-routine/personalized`);
      setSpiritualRoutine(response.data);
      toast.success("Personalized routine generated!");
    } catch (error) {
      toast.error("Failed to generate personalized routine");
    } finally {
      setLoadingSpiritual(false);
    }
  };

  // Fitness Functions
  const fetchFitnessOptions = async () => {
    try {
      const response = await axios.get(`${API}/fitness/options`);
      setFitnessOptions(response.data);
    } catch (error) {
      console.error("Failed to load fitness options");
    }
  };

  const fetchFitnessProfile = async () => {
    try {
      const response = await axios.get(`${API}/fitness/profile`);
      if (response.data && Object.keys(response.data).length > 0) {
        setFitnessProfile(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to load fitness profile");
    }
  };

  const saveFitnessProfile = async () => {
    setSavingFitness(true);
    try {
      await axios.put(`${API}/fitness/profile`, {
        ...fitnessProfile,
        current_weight: fitnessProfile.current_weight ? parseFloat(fitnessProfile.current_weight) : null,
        target_weight: fitnessProfile.target_weight ? parseFloat(fitnessProfile.target_weight) : null,
      });
      toast.success("Fitness profile saved!");
    } catch (error) {
      toast.error("Failed to save fitness profile");
    } finally {
      setSavingFitness(false);
    }
  };

  const generateWorkoutPlan = async () => {
    setLoadingWorkout(true);
    try {
      const response = await axios.post(`${API}/fitness/workout-plan`);
      setWorkoutPlan(response.data.workout_plan);
      toast.success("Workout plan generated! 💪");
    } catch (error) {
      toast.error("Failed to generate workout plan");
    } finally {
      setLoadingWorkout(false);
    }
  };

  const generateMealPlan = async () => {
    setLoadingMeal(true);
    try {
      const response = await axios.post(`${API}/fitness/meal-plan`);
      setMealPlan(response.data.meal_plan);
      toast.success("Meal plan generated! 🥗");
    } catch (error) {
      toast.error("Failed to generate meal plan");
    } finally {
      setLoadingMeal(false);
    }
  };

  const toggleFitnessGoal = (goal) => {
    setFitnessProfile(prev => ({
      ...prev,
      fitness_goals: prev.fitness_goals.includes(goal)
        ? prev.fitness_goals.filter(g => g !== goal)
        : [...prev.fitness_goals, goal]
    }));
  };

  const toggleWorkoutType = (type) => {
    setFitnessProfile(prev => ({
      ...prev,
      preferred_workouts: prev.preferred_workouts.includes(type)
        ? prev.preferred_workouts.filter(t => t !== type)
        : [...prev.preferred_workouts, type]
    }));
  };

  const handleSignChange = (sign) => {
    setSelectedSign(sign);
    fetchHoroscope(sign);
  };

  useEffect(() => {
    fetchFitnessOptions();
    fetchFitnessProfile();
  }, []);

  useEffect(() => {
    if (selectedSign) {
      fetchHoroscope(selectedSign);
    }
  }, [selectedSign]);

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-primary/10 to-secondary/10"></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-primary flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Life Training
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Daily inspiration, mindfulness practices, and tools for your journey
            </p>
          </div>

          {/* Wellness Score */}
          {wellnessStats && (
            <div className="card-happy max-w-md mx-auto text-center">
              <p className="text-sm text-muted-foreground mb-2">Your Wellness Score</p>
              <div className="text-4xl font-bold text-primary mb-2">{wellnessStats.wellness_score}</div>
              <Progress value={wellnessStats.wellness_score} className="h-2" />
              <div className="flex justify-center gap-6 mt-4 text-sm">
                <div>
                  <span className="font-semibold">{wellnessStats.breathing_sessions}</span>
                  <span className="text-muted-foreground ml-1">sessions</span>
                </div>
                <div>
                  <span className="font-semibold">{wellnessStats.gratitude_entries}</span>
                  <span className="text-muted-foreground ml-1">gratitudes</span>
                </div>
                <div>
                  <span className="font-semibold">{wellnessStats.goals_completed}</span>
                  <span className="text-muted-foreground ml-1">goals</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="daily" className="space-y-8">
          <ScrollArea className="w-full">
            <TabsList className="flex w-max gap-1 mx-auto mb-4">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="financial">Financial</TabsTrigger>
              <TabsTrigger value="spiritual">Spiritual</TabsTrigger>
              <TabsTrigger value="fitness">Fitness</TabsTrigger>
              <TabsTrigger value="breathing">Breathing</TabsTrigger>
              <TabsTrigger value="consciousness">Mind</TabsTrigger>
              <TabsTrigger value="hypnosis">Hypnosis</TabsTrigger>
              <TabsTrigger value="itinerary">Planner</TabsTrigger>
            </TabsList>
          </ScrollArea>

          {/* Daily Tab - Affirmations & Horoscope */}
          <TabsContent value="daily" className="space-y-8">
            {/* Daily Affirmation */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Sun className="w-6 h-6 text-primary" />
                Today's Affirmation
              </h2>
              <div className="card-happy bg-gradient-to-r from-primary/5 to-secondary/5 text-center py-8">
                <p className="text-2xl md:text-3xl font-handwriting text-foreground leading-relaxed max-w-2xl mx-auto">
                  "{affirmation}"
                </p>
                <div className="mt-6 flex justify-center gap-4 flex-wrap">
                  <ReadAloudButton 
                    text={affirmation} 
                    variant="outline"
                  />
                  <Button
                    onClick={fetchPersonalizedAffirmation}
                    variant="outline"
                    className="rounded-full"
                    data-testid="personalized-affirmation-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Get Personalized
                  </Button>
                </div>
                {personalizedAffirmation && (
                  <div className="mt-6 p-4 bg-white/50 rounded-2xl max-w-xl mx-auto">
                    <p className="text-lg font-handwriting text-primary">
                      "{personalizedAffirmation.affirmation}"
                    </p>
                    {personalizedAffirmation.based_on_goals && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Based on your goals: {personalizedAffirmation.based_on_goals.join(", ")}
                      </p>
                    )}
                    <div className="mt-3">
                      <ReadAloudButton 
                        text={personalizedAffirmation.affirmation} 
                        size="sm"
                        variant="ghost"
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Horoscope */}
            <section>
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Star className="w-6 h-6 text-purple-500" />
                Daily Horoscope
              </h2>
              <div className="card-happy">
                <div className="flex flex-wrap items-center gap-4 mb-6">
                  <span className="text-muted-foreground">Select your sign:</span>
                  <Select value={selectedSign} onValueChange={handleSignChange}>
                    <SelectTrigger className="w-48 input-happy" data-testid="zodiac-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ZODIAC_SIGNS.map((sign) => {
                        const Icon = ZODIAC_ICONS[sign];
                        return (
                          <SelectItem key={sign} value={sign}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-4 h-4" />
                              <span className="capitalize">{sign}</span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                {horoscope && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                        {(() => {
                          const Icon = ZODIAC_ICONS[selectedSign];
                          return <Icon className="w-8 h-8 text-purple-600" />;
                        })()}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold">{horoscope.sign}</h3>
                        <p className="text-sm text-muted-foreground">{horoscope.dates}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary">{horoscope.element}</Badge>
                          <span className="text-sm text-muted-foreground">{horoscope.traits}</span>
                        </div>
                      </div>
                    </div>
                    <p className="text-foreground leading-relaxed">{horoscope.horoscope}</p>
                    <div className="mt-4 pt-4 border-t">
                      <ReadAloudButton 
                        text={`${horoscope.sign} horoscope. ${horoscope.horoscope}`} 
                        variant="outline"
                        className="w-full justify-center"
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>
          </TabsContent>

          {/* Financial Advisor Tab */}
          <TabsContent value="financial" className="space-y-6">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Wallet className="w-6 h-6 text-green-500" />
              Financial Advisor
            </h2>
            <p className="text-muted-foreground">
              Get personalized financial advice to help you reach your goals and get back in the green.
            </p>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Input Form */}
              <div className="card-happy">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Your Financial Info
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="income">Monthly Income ($)</Label>
                      <Input
                        id="income"
                        type="number"
                        placeholder="e.g., 4000"
                        value={financialInfo.monthly_income}
                        onChange={(e) => setFinancialInfo({...financialInfo, monthly_income: e.target.value})}
                        className="input-happy"
                        data-testid="financial-income"
                      />
                    </div>
                    <div>
                      <Label htmlFor="expenses">Monthly Expenses ($)</Label>
                      <Input
                        id="expenses"
                        type="number"
                        placeholder="e.g., 3000"
                        value={financialInfo.monthly_expenses}
                        onChange={(e) => setFinancialInfo({...financialInfo, monthly_expenses: e.target.value})}
                        className="input-happy"
                        data-testid="financial-expenses"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="debt">Total Debt ($)</Label>
                      <Input
                        id="debt"
                        type="number"
                        placeholder="e.g., 5000"
                        value={financialInfo.debt_amount}
                        onChange={(e) => setFinancialInfo({...financialInfo, debt_amount: e.target.value})}
                        className="input-happy"
                        data-testid="financial-debt"
                      />
                    </div>
                    <div>
                      <Label htmlFor="savings">Savings Goal ($)</Label>
                      <Input
                        id="savings"
                        type="number"
                        placeholder="e.g., 10000"
                        value={financialInfo.savings_goal}
                        onChange={(e) => setFinancialInfo({...financialInfo, savings_goal: e.target.value})}
                        className="input-happy"
                        data-testid="financial-savings"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="situation">Describe Your Situation (Optional)</Label>
                    <Textarea
                      id="situation"
                      placeholder="Tell us about your financial challenges or goals..."
                      value={financialInfo.current_situation}
                      onChange={(e) => setFinancialInfo({...financialInfo, current_situation: e.target.value})}
                      className="input-happy min-h-[100px]"
                      data-testid="financial-situation"
                    />
                  </div>
                  <Button
                    onClick={fetchFinancialAdvice}
                    className="btn-primary w-full"
                    disabled={loadingFinancial}
                    data-testid="get-financial-advice-btn"
                  >
                    {loadingFinancial ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating Advice...
                      </>
                    ) : (
                      <>
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Get Financial Advice
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Advice Display */}
              <div className="space-y-4">
                {financialAdvice ? (
                  <>
                    <div className="card-happy bg-gradient-to-r from-green-50 to-emerald-50">
                      <h3 className="font-semibold mb-2 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-green-500" />
                        Your Financial Overview
                      </h3>
                      <p className="text-foreground">{financialAdvice.advice}</p>
                    </div>

                    <div className="card-happy">
                      <h3 className="font-semibold mb-3 flex items-center gap-2">
                        <Target className="w-5 h-5 text-primary" />
                        Action Plan
                      </h3>
                      <ol className="space-y-2">
                        {financialAdvice.action_plan?.map((step, i) => (
                          <li key={i} className="flex gap-3">
                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm shrink-0">
                              {i + 1}
                            </span>
                            <span className="text-sm">{step}</span>
                          </li>
                        ))}
                      </ol>
                    </div>

                    {financialAdvice.budget_breakdown && (
                      <div className="card-happy">
                        <h3 className="font-semibold mb-3 flex items-center gap-2">
                          <PiggyBank className="w-5 h-5 text-amber-500" />
                          Suggested Budget
                        </h3>
                        <div className="space-y-2">
                          {Object.entries(financialAdvice.budget_breakdown).map(([category, percentage]) => (
                            <div key={category} className="flex justify-between items-center">
                              <span className="text-sm">{category}</span>
                              <Badge variant="outline">{percentage}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="card-happy">
                      <h3 className="font-semibold mb-3">Savings Tips</h3>
                      <ul className="space-y-2">
                        {financialAdvice.savings_tips?.map((tip, i) => (
                          <li key={i} className="flex gap-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {financialAdvice.debt_strategy && (
                      <div className="card-happy bg-amber-50">
                        <h3 className="font-semibold mb-2">Debt Reduction Strategy</h3>
                        <p className="text-sm">{financialAdvice.debt_strategy}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="card-happy text-center py-12">
                    <Wallet className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Get Personalized Financial Advice</h3>
                    <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                      Enter your financial information and we'll create a customized plan to help you achieve your goals.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Spiritual Daily Routine Tab */}
          <TabsContent value="spiritual" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-semibold flex items-center gap-2">
                  <Heart className="w-6 h-6 text-purple-500" />
                  Spiritual Daily Routine
                </h2>
                <p className="text-muted-foreground">
                  Raise your vibration and nourish your soul with these daily practices.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={fetchSpiritualRoutine}
                  variant="outline"
                  className="rounded-full"
                  disabled={loadingSpiritual}
                  data-testid="load-spiritual-btn"
                >
                  {loadingSpiritual ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={fetchPersonalizedSpiritualRoutine}
                  className="btn-primary"
                  disabled={loadingSpiritual}
                  data-testid="personalized-spiritual-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Personalize
                </Button>
              </div>
            </div>

            {spiritualRoutine ? (
              <div className="space-y-6">
                {spiritualRoutine.daily_focus && (
                  <div className="card-happy bg-gradient-to-r from-purple-50 to-pink-50 text-center">
                    <p className="text-lg font-handwriting text-purple-700">
                      "{spiritualRoutine.daily_focus}"
                    </p>
                  </div>
                )}

                {/* Practices Timeline */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Daily Practices</h3>
                  {spiritualRoutine.practices?.map((practice, index) => (
                    <div key={practice.id || index} className="card-happy">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                          <Clock className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                            <h4 className="font-semibold">{practice.name}</h4>
                            <div className="flex gap-2">
                              <Badge variant="secondary">{practice.time}</Badge>
                              <Badge variant="outline">{practice.duration}</Badge>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground mb-3">{practice.description}</p>
                          
                          {practice.steps && (
                            <div className="bg-muted/50 rounded-xl p-4">
                              <h5 className="text-sm font-medium mb-2">Steps:</h5>
                              <ol className="space-y-1">
                                {practice.steps.map((step, i) => (
                                  <li key={i} className="text-sm flex gap-2">
                                    <span className="text-purple-500 font-medium">{i + 1}.</span>
                                    {step}
                                  </li>
                                ))}
                              </ol>
                            </div>
                          )}

                          {practice.affirmations && (
                            <div className="mt-3 p-3 bg-purple-50 rounded-xl">
                              <h5 className="text-sm font-medium mb-2">Affirmations:</h5>
                              <ul className="space-y-1">
                                {practice.affirmations.map((aff, i) => (
                                  <li key={i} className="text-sm italic text-purple-700">"{aff}"</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {practice.benefits && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {practice.benefits.map((benefit, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{benefit}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Frequency Tips */}
                {spiritualRoutine.frequency_tips && (
                  <div className="card-happy">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      Tips to Raise Your Vibration
                    </h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {spiritualRoutine.frequency_tips.map((tip, i) => (
                        <div key={i} className="flex gap-2 text-sm">
                          <Star className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                          {tip}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="card-happy text-center py-12">
                <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Discover Your Spiritual Routine</h3>
                <p className="text-muted-foreground text-sm max-w-sm mx-auto mb-6">
                  Get daily practices designed to raise your vibration and nurture your soul.
                </p>
                <Button onClick={fetchSpiritualRoutine} className="btn-primary">
                  Load Spiritual Routine
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Fitness Tab */}
          <TabsContent value="fitness" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-semibold flex items-center gap-2">
                  <Dumbbell className="w-6 h-6 text-orange-500" />
                  Fitness Center
                </h2>
                <p className="text-muted-foreground">
                  AI-powered workout and meal plans tailored to your goals
                </p>
              </div>
            </div>

            <Tabs defaultValue="profile" className="w-full">
              <TabsList className="grid w-full grid-cols-3 max-w-md">
                <TabsTrigger value="profile">My Profile</TabsTrigger>
                <TabsTrigger value="workout">Workout Plan</TabsTrigger>
                <TabsTrigger value="meals">Meal Plan</TabsTrigger>
              </TabsList>

              {/* Fitness Profile Tab */}
              <TabsContent value="profile" className="space-y-6 mt-6">
                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Basic Info */}
                  <div className="card-happy">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Activity className="w-5 h-5 text-orange-500" />
                      Your Fitness Profile
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Fitness Level</Label>
                        <div className="flex gap-2 mt-2">
                          {(fitnessOptions.fitness_levels || ["beginner", "intermediate", "advanced"]).map(level => (
                            <Button
                              key={level}
                              variant={fitnessProfile.fitness_level === level ? "default" : "outline"}
                              onClick={() => setFitnessProfile(prev => ({ ...prev, fitness_level: level }))}
                              className="capitalize flex-1"
                              data-testid={`fitness-level-${level}`}
                            >
                              {level}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="current-weight">Current Weight (kg)</Label>
                          <Input
                            id="current-weight"
                            type="number"
                            placeholder="70"
                            value={fitnessProfile.current_weight}
                            onChange={(e) => setFitnessProfile(prev => ({ ...prev, current_weight: e.target.value }))}
                            className="input-happy"
                            data-testid="fitness-current-weight"
                          />
                        </div>
                        <div>
                          <Label htmlFor="target-weight">Target Weight (kg)</Label>
                          <Input
                            id="target-weight"
                            type="number"
                            placeholder="65"
                            value={fitnessProfile.target_weight}
                            onChange={(e) => setFitnessProfile(prev => ({ ...prev, target_weight: e.target.value }))}
                            className="input-happy"
                            data-testid="fitness-target-weight"
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Workout Frequency</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(fitnessOptions.frequency_options || ["1-2x/week", "3x/week", "4-5x/week", "Daily"]).map(freq => (
                            <Button
                              key={freq}
                              variant={fitnessProfile.workout_frequency === freq ? "default" : "outline"}
                              onClick={() => setFitnessProfile(prev => ({ ...prev, workout_frequency: freq }))}
                              size="sm"
                            >
                              {freq}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Goals */}
                  <div className="card-happy">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-green-500" />
                      Your Fitness Goals
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {(fitnessOptions.fitness_goals || []).map(goal => (
                        <Badge
                          key={goal}
                          variant={fitnessProfile.fitness_goals.includes(goal) ? "default" : "outline"}
                          className="cursor-pointer py-2 px-3"
                          onClick={() => toggleFitnessGoal(goal)}
                          data-testid={`fitness-goal-${goal.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {fitnessProfile.fitness_goals.includes(goal) && (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          )}
                          {goal}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Preferred Workouts */}
                  <div className="card-happy">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Dumbbell className="w-5 h-5 text-blue-500" />
                      Preferred Workout Types
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {(fitnessOptions.workout_types || []).map(type => (
                        <Badge
                          key={type}
                          variant={fitnessProfile.preferred_workouts.includes(type) ? "default" : "outline"}
                          className="cursor-pointer py-2 px-3"
                          onClick={() => toggleWorkoutType(type)}
                        >
                          {fitnessProfile.preferred_workouts.includes(type) && (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          )}
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Dietary Restrictions */}
                  <div className="card-happy">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Apple className="w-5 h-5 text-red-500" />
                      Dietary Preferences
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {["Vegetarian", "Vegan", "Gluten-Free", "Dairy-Free", "Keto", "Paleo", "Low-Carb", "High-Protein"].map(diet => (
                        <Badge
                          key={diet}
                          variant={fitnessProfile.dietary_restrictions?.includes(diet) ? "default" : "outline"}
                          className="cursor-pointer py-2 px-3"
                          onClick={() => setFitnessProfile(prev => ({
                            ...prev,
                            dietary_restrictions: prev.dietary_restrictions?.includes(diet)
                              ? prev.dietary_restrictions.filter(d => d !== diet)
                              : [...(prev.dietary_restrictions || []), diet]
                          }))}
                        >
                          {fitnessProfile.dietary_restrictions?.includes(diet) && (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          )}
                          {diet}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <Button
                  onClick={saveFitnessProfile}
                  disabled={savingFitness}
                  className="btn-primary w-full max-w-md mx-auto block"
                  data-testid="save-fitness-profile-btn"
                >
                  {savingFitness ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Save Fitness Profile
                    </>
                  )}
                </Button>
              </TabsContent>

              {/* Workout Plan Tab */}
              <TabsContent value="workout" className="space-y-6 mt-6">
                <div className="flex justify-between items-center">
                  <p className="text-muted-foreground">
                    Get a personalized weekly workout plan based on your profile
                  </p>
                  <Button
                    onClick={generateWorkoutPlan}
                    disabled={loadingWorkout}
                    className="btn-primary"
                    data-testid="generate-workout-btn"
                  >
                    {loadingWorkout ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Plan
                      </>
                    )}
                  </Button>
                </div>

                {workoutPlan ? (
                  <div className="space-y-6">
                    {workoutPlan.overview && (
                      <div className="card-happy bg-gradient-to-r from-orange-50 to-amber-50">
                        <p className="text-foreground">{workoutPlan.overview}</p>
                      </div>
                    )}

                    {/* Weekly Plan */}
                    <div className="space-y-4">
                      {workoutPlan.weekly_plan?.map((day, index) => (
                        <div key={index} className="card-happy">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-lg">{day.day}</h4>
                            <div className="flex gap-2">
                              <Badge variant="secondary">{day.workout}</Badge>
                              <Badge variant="outline">{day.duration}</Badge>
                            </div>
                          </div>
                          {day.exercises && (
                            <div className="space-y-2">
                              {day.exercises.map((exercise, i) => (
                                <div key={i} className="flex justify-between items-center text-sm bg-muted/50 rounded-lg p-2">
                                  <span>{exercise.name}</span>
                                  <span className="text-muted-foreground">
                                    {exercise.sets && `${exercise.sets} × `}{exercise.reps}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Motivation Tips */}
                    {workoutPlan.motivation_tips && (
                      <div className="card-happy">
                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                          <Star className="w-5 h-5 text-amber-500" />
                          Stay Motivated
                        </h4>
                        <ul className="space-y-2">
                          {workoutPlan.motivation_tips.map((tip, i) => (
                            <li key={i} className="flex gap-2 text-sm">
                              <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="card-happy text-center py-12">
                    <Dumbbell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Get Your Personalized Workout Plan</h3>
                    <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                      Fill out your fitness profile and click &ldquo;Generate Plan&rdquo; to get an AI-powered workout routine.
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* Meal Plan Tab */}
              <TabsContent value="meals" className="space-y-6 mt-6">
                <div className="flex justify-between items-center">
                  <p className="text-muted-foreground">
                    Get a balanced meal plan to support your fitness goals
                  </p>
                  <Button
                    onClick={generateMealPlan}
                    disabled={loadingMeal}
                    className="btn-primary"
                    data-testid="generate-meal-btn"
                  >
                    {loadingMeal ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Apple className="w-4 h-4 mr-2" />
                        Generate Meal Plan
                      </>
                    )}
                  </Button>
                </div>

                {mealPlan ? (
                  <div className="space-y-6">
                    {mealPlan.overview && (
                      <div className="card-happy bg-gradient-to-r from-green-50 to-emerald-50">
                        <p className="text-foreground">{mealPlan.overview}</p>
                      </div>
                    )}

                    {/* Daily Meals */}
                    {mealPlan.meal_plan?.slice(0, 3).map((day, index) => (
                      <div key={index} className="card-happy">
                        <h4 className="font-semibold text-lg mb-4">{day.day}</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="bg-amber-50 rounded-xl p-3">
                            <p className="text-xs text-amber-600 font-medium mb-1">Breakfast</p>
                            <p className="font-medium">{day.breakfast?.name}</p>
                            <p className="text-sm text-muted-foreground">{day.breakfast?.description}</p>
                          </div>
                          <div className="bg-green-50 rounded-xl p-3">
                            <p className="text-xs text-green-600 font-medium mb-1">Lunch</p>
                            <p className="font-medium">{day.lunch?.name}</p>
                            <p className="text-sm text-muted-foreground">{day.lunch?.description}</p>
                          </div>
                          <div className="bg-blue-50 rounded-xl p-3">
                            <p className="text-xs text-blue-600 font-medium mb-1">Dinner</p>
                            <p className="font-medium">{day.dinner?.name}</p>
                            <p className="text-sm text-muted-foreground">{day.dinner?.description}</p>
                          </div>
                          {day.snacks && (
                            <div className="bg-purple-50 rounded-xl p-3">
                              <p className="text-xs text-purple-600 font-medium mb-1">Snacks</p>
                              {day.snacks.map((snack, i) => (
                                <p key={i} className="text-sm">{snack.name}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Tips */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {mealPlan.hydration_tips && (
                        <div className="card-happy">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <Droplets className="w-5 h-5 text-blue-500" />
                            Hydration Tips
                          </h4>
                          <ul className="space-y-2">
                            {mealPlan.hydration_tips.slice(0, 4).map((tip, i) => (
                              <li key={i} className="flex gap-2 text-sm">
                                <CheckCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                                {tip}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {mealPlan.healthy_treats && (
                        <div className="card-happy">
                          <h4 className="font-semibold mb-3 flex items-center gap-2">
                            <Heart className="w-5 h-5 text-pink-500" />
                            Healthy Treats
                          </h4>
                          <ul className="space-y-2">
                            {mealPlan.healthy_treats.map((treat, i) => (
                              <li key={i} className="flex gap-2 text-sm">
                                <Star className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                                {treat}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="card-happy text-center py-12">
                    <Apple className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Get Your Personalized Meal Plan</h3>
                    <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                      Click &ldquo;Generate Meal Plan&rdquo; to get healthy, balanced meals tailored to your goals.
                    </p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Breathing Tab */}
          <TabsContent value="breathing" className="space-y-6">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Wind className="w-6 h-6 text-cyan-500" />
              Breathing Exercises
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {breathingExercises && breathingExercises.length > 0 ? breathingExercises.map((exercise) => (
                <div
                  key={exercise.id}
                  className="card-happy cursor-pointer hover:scale-105 transition-all"
                  onClick={() => setActiveBreathingExercise(exercise)}
                  data-testid={`breathing-${exercise.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="secondary">{exercise.difficulty}</Badge>
                    <span className="text-sm text-muted-foreground">{exercise.duration}</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{exercise.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{exercise.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {exercise.benefits && exercise.benefits.map((benefit, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {benefit}
                      </Badge>
                    ))}
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-center py-8">
                  <p className="text-muted-foreground">Loading breathing exercises...</p>
                </div>
              )}
            </div>

            {/* Breathing Exercise Modal */}
            <Dialog open={!!activeBreathingExercise} onOpenChange={() => setActiveBreathingExercise(null)}>
              <DialogContent className="max-w-lg">
                {activeBreathingExercise && (
                  <BreathingExercisePlayer
                    exercise={activeBreathingExercise}
                    onComplete={() => {
                      toast.success("Great job! Session logged.");
                      setActiveBreathingExercise(null);
                    }}
                  />
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Consciousness Tab */}
          <TabsContent value="consciousness" className="space-y-6">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Brain className="w-6 h-6 text-indigo-500" />
              Consciousness & Mindfulness
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {consciousnessPractices && consciousnessPractices.length > 0 ? consciousnessPractices.map((practice) => (
                <div
                  key={practice.id}
                  className="card-happy cursor-pointer hover:shadow-hover transition-all"
                  onClick={() => setSelectedPractice(practice)}
                  data-testid={`practice-${practice.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge className="bg-indigo-100 text-indigo-700">{practice.category}</Badge>
                    <span className="text-sm text-muted-foreground">{practice.duration}</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{practice.name}</h3>
                  <p className="text-sm text-muted-foreground">{practice.description}</p>
                  <div className="flex items-center gap-2 mt-4 text-primary">
                    <span className="text-sm font-medium">Learn more</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              )) : (
                <div className="col-span-full text-center py-8">
                  <p className="text-muted-foreground">Loading consciousness practices...</p>
                </div>
              )}
            </div>

            {/* Practice Detail Modal */}
            <Dialog open={!!selectedPractice} onOpenChange={() => setSelectedPractice(null)}>
              <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                {selectedPractice && (
                  <>
                    <DialogHeader>
                      <DialogTitle>{selectedPractice.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-indigo-100 text-indigo-700">{selectedPractice.category}</Badge>
                        <span className="text-sm text-muted-foreground">{selectedPractice.duration}</span>
                      </div>
                      <p className="text-muted-foreground">{selectedPractice.description}</p>
                      
                      <div>
                        <h4 className="font-semibold mb-2">Steps</h4>
                        <ol className="space-y-2">
                          {selectedPractice.steps && selectedPractice.steps.map((step, i) => (
                            <li key={i} className="flex gap-3">
                              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm shrink-0">
                                {i + 1}
                              </span>
                              <span className="text-sm">{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>

                      <div>
                        <h4 className="font-semibold mb-2">Benefits</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedPractice.benefits && selectedPractice.benefits.map((benefit, i) => (
                            <Badge key={i} variant="outline">{benefit}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Hypnosis Tab */}
          <TabsContent value="hypnosis" className="space-y-6">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <Eye className="w-6 h-6 text-purple-500" />
              Hypnosis Awareness
            </h2>
            <p className="text-muted-foreground max-w-2xl">
              Learn about the power of self-hypnosis for positive change. These guides will help you understand 
              and safely practice techniques for relaxation, focus, and personal transformation.
            </p>
            <div className="space-y-4">
              {hypnosisGuides && hypnosisGuides.length > 0 ? hypnosisGuides.map((guide, index) => (
                <div key={guide.id} className="card-happy" data-testid={`hypnosis-${guide.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                      <span className="text-purple-600 font-bold">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold">{guide.name}</h3>
                        <Badge variant="secondary">{guide.category}</Badge>
                      </div>
                      <p className="text-muted-foreground mb-4">{guide.description}</p>
                      
                      {guide.key_points && guide.key_points.length > 0 && (
                        <div className="mb-4">
                          <h4 className="font-medium text-sm mb-2">Key Points:</h4>
                          <ul className="space-y-1">
                            {guide.key_points.map((point, i) => (
                              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                <Heart className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                {point}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {guide.steps && guide.steps.length > 0 && (
                        <div>
                          <h4 className="font-medium text-sm mb-2">Practice Steps:</h4>
                          <ol className="space-y-1">
                            {guide.steps.map((step, i) => (
                              <li key={i} className="text-sm flex gap-2">
                                <span className="text-primary font-medium">{i + 1}.</span>
                                {step}
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {guide.examples && guide.examples.length > 0 && (
                        <div className="mt-4 p-4 bg-purple-50 rounded-xl">
                          <h4 className="font-medium text-sm mb-2">Example Suggestions:</h4>
                          <ul className="space-y-1">
                            {guide.examples.map((ex, i) => (
                              <li key={i} className="text-sm italic text-purple-700">"{ex}"</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">Loading hypnosis guides...</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Itinerary Tab */}
          <TabsContent value="itinerary" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold flex items-center gap-2">
                <Calendar className="w-6 h-6 text-green-500" />
                Daily Goal Itinerary
              </h2>
              <Button
                onClick={fetchItinerary}
                className="btn-primary"
                data-testid="generate-itinerary-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Generate My Plan
              </Button>
            </div>

            {!itinerary ? (
              <div className="card-happy text-center py-12">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Get Your Personalized Daily Plan</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Based on your active goals, we'll create a day-by-day itinerary with specific times 
                  and activities to help you make progress.
                </p>
                <Button onClick={fetchItinerary} className="btn-primary">
                  Generate Itinerary
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {itinerary.goals && itinerary.goals.length > 0 && (
                  <div className="card-happy bg-green-50">
                    <h3 className="font-semibold mb-2">Today's Focus Goals:</h3>
                    <div className="flex flex-wrap gap-2">
                      {itinerary.goals.map((goal, i) => (
                        <Badge key={i} className="bg-green-100 text-green-700">{goal}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-3" data-testid="itinerary-list">
                  {itinerary.itinerary && itinerary.itinerary.map((item, index) => (
                    <div
                      key={index}
                      className="card-happy flex items-start gap-4"
                    >
                      <div className="text-center shrink-0 w-20">
                        <p className="font-semibold text-primary">{item.time}</p>
                        <p className="text-xs text-muted-foreground">{item.duration}</p>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium">{item.activity}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{item.tip}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {!itinerary.ai_generated && (
                  <p className="text-center text-sm text-muted-foreground">
                    This is a template itinerary. Add more goals for a personalized AI-generated plan!
                  </p>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Navigation />
    </div>
  );
}

// Breathing Exercise Player Component
function BreathingExercisePlayer({ exercise, onComplete }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentCycle, setCurrentCycle] = useState(1);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const intervalRef = useRef(null);

  const step = exercise.steps[currentStep];

  useEffect(() => {
    if (isPlaying && step) {
      setTimeLeft(step.duration);
      
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            // Move to next step
            const nextStep = currentStep + 1;
            if (nextStep >= exercise.steps.length) {
              // Cycle complete
              if (currentCycle >= exercise.cycles) {
                // Exercise complete
                clearInterval(intervalRef.current);
                setIsPlaying(false);
                logSession();
                onComplete();
                return 0;
              }
              setCurrentCycle((c) => c + 1);
              setCurrentStep(0);
            } else {
              setCurrentStep(nextStep);
            }
            return exercise.steps[nextStep % exercise.steps.length]?.duration || 0;
          }
          setTotalTime((t) => t + 1);
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(intervalRef.current);
  }, [isPlaying, currentStep, currentCycle]);

  const logSession = async () => {
    try {
      await axios.post(`${API}/wellness/breathing-session?exercise_id=${exercise.id}&duration_seconds=${totalTime}`);
    } catch (e) {
      console.error("Failed to log session");
    }
  };

  const handlePlayPause = () => {
    if (!isPlaying && timeLeft === 0) {
      setTimeLeft(step?.duration || 4);
    }
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    clearInterval(intervalRef.current);
    setIsPlaying(false);
    setCurrentStep(0);
    setCurrentCycle(1);
    setTimeLeft(0);
    setTotalTime(0);
  };

  const progress = step ? ((step.duration - timeLeft) / step.duration) * 100 : 0;

  return (
    <div className="text-center py-4">
      <h2 className="text-2xl font-semibold mb-2">{exercise.name}</h2>
      <p className="text-muted-foreground mb-6">{exercise.description}</p>

      {/* Visual indicator */}
      <div className="relative w-48 h-48 mx-auto mb-6">
        <div
          className={`absolute inset-0 rounded-full transition-all duration-1000 ${
            step?.action === "Inhale" ? "scale-100 bg-cyan-100" :
            step?.action === "Exhale" ? "scale-75 bg-cyan-50" :
            "scale-90 bg-cyan-75"
          }`}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-3xl font-bold text-cyan-600">{timeLeft}</p>
          <p className="text-lg font-medium text-cyan-700">{step?.action || "Ready"}</p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        {step?.instruction || "Press play to begin"}
      </p>

      <div className="flex items-center justify-center gap-4 mb-4">
        <span className="text-sm text-muted-foreground">
          Cycle {currentCycle} of {exercise.cycles}
        </span>
      </div>

      <Progress value={progress} className="h-2 mb-6" />

      <div className="flex justify-center gap-4">
        <Button
          onClick={handlePlayPause}
          className="btn-primary"
          data-testid="breathing-play-btn"
        >
          {isPlaying ? <Pause className="w-5 h-5 mr-2" /> : <Play className="w-5 h-5 mr-2" />}
          {isPlaying ? "Pause" : "Start"}
        </Button>
        <Button
          onClick={handleReset}
          variant="outline"
          className="rounded-full"
        >
          <RefreshCw className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );
}
